// client/src/features/compliance-calendar/sources.ts — the five lanes of the unified compliance
// calendar · MAINT-009 (05 MAINT-009 criterion 1; 01 §4 digest #10/#12; 04 §8.11).
//
// 01 §4's compliance-expiration digest asks for „one unified compliance calendar merging
// insurance + revisions + contract expiries + fiscal deadlines", 05 MAINT-009 makes that four
// V1 sources with „fiscal source slot present (empty until M4)", and this file is where the
// four become one vocabulary.
//
// EVERY LANE IS AN EXISTING READ. Nothing here is a new endpoint, a join done in the browser,
// or a second opinion about what is due:
//
//   • INSURANCE — `GET /api/v1/insurances`, `endDate` and the server's own `daysUntilExpiry`
//     (`InsuranceStore` computes it as `EndDate.DayNumber - today.DayNumber` against
//     `IClock.TodayInRomania`).
//   • REVISION  — `GET /api/v1/equipment`, `nextRevisionDate` and `daysUntilRevision`, both
//     `EquipmentStore`'s. NOT `/api/v1/maintenance-tasks`: that endpoint does not exist
//     (D-1102), and the rows behind it are ladder RUNGS rather than deadlines — the deadline a
//     landlord owes is the scadență, and it is on the equipment row. See DEVIATIONS D-1133.
//   • CONTRACT  — `GET /api/v1/contracts?status=ACTIVE`, `endDate` and `daysUntilEnd`, both
//     `ContractRules`'.
//   • DOCUMENT  — `expiringDocumentsQueryOptions` from `features/documents/expiring.ts`, which
//     is DOC-004's own query and not a copy of it: 05 DOC-004's second criterion („the
//     expiring-documents view feeds from the same data as the calendar chips") is a claim about
//     THIS import. The day count is the one figure the vault does not publish (D-1142).
//   • FISCAL    — `GET /api/v1/fiscal/calendar`, FISC-003's, through FISC-007's own
//     `useFiscalCalendar`. Wired by the item D-281 named; the lane was a labelled empty slot
//     until M4 and D-1134 records both halves of that. Every string it renders is composed by
//     the server (`titleRo`), and `daysRemaining` is the count FISC-003 already decided against
//     the SHIFTED date — a weekend-shifted term is counted to the day it actually ends.
//
// THE COLOUR IS THE NOTIFICATION ENGINE'S, PER FAMILY. `revisionPriority` is
// `NotificationPriorityPolicy.ForCompliance` restated on the client — CRITICAL once the date has
// passed, IMPORTANT while it is ahead and inside the organization's widest rung, no colour at
// all further out — and each lane asks it with ITS OWN family's ladder, read from
// `app.notification_rules` rather than from a constant (D-942's mistake, one layer up). A
// contract ending in eleven months is on the calendar and is not coloured, because the engine
// has not reached it either.
//
// NOTHING HERE COUNTS OR RE-DATES ANYTHING. Every `daysUntil` but the document lane's came off
// the wire; every date is the server's column; membership in the window is the server's filter
// where the endpoint has one and a string comparison of two `YYYY-MM-DD` values where it does
// not (D-705).

import type {
  ContractListItem,
  DocumentView,
  EquipmentListItem,
  FiscalCalendarEvent,
  InsuranceListItem,
  NotificationPriority,
} from '@/client';
import { daysUntilExpiration } from '@/features/documents/expiring';
import {
  INSURANCE_RULE_TYPE,
  REVISION_RULE_TYPE,
  revisionPriority,
  type RevisionLadder,
} from '@/features/equipment/ladder';
import { customTypeName } from '@/features/equipment/labels';
import { wireNumber, wireNumberOrNull } from '@/lib/wire';

/**
 * The five lanes, in the order 01 §4 lists them and the legend renders them. `FISCAL` was
 * registered and empty in M3 — that is what „slot present" meant — and reads its source from
 * M4 on.
 */
export const COMPLIANCE_SOURCES = [
  'INSURANCE',
  'REVISION',
  'CONTRACT',
  'DOCUMENT',
  'FISCAL',
] as const;

export type ComplianceSource = (typeof COMPLIANCE_SOURCES)[number];

/**
 * 03 §15.2's rule family each lane's colour is read from — the row of
 * `GET /api/v1/notification-rules` whose ladder decides when a deadline starts being worth a
 * chip. `FISCAL_OBLIGATION` ships as 30/14/7/3 (`NotificationRuleCatalog.Defaults`), which is a
 * narrower ladder than the 60/30/14/7 the other four families get — a fiscal deadline goes
 * amber a month out and not two.
 */
export const SOURCE_RULE_TYPE: Record<ComplianceSource, string> = {
  INSURANCE: INSURANCE_RULE_TYPE,
  REVISION: REVISION_RULE_TYPE,
  CONTRACT: 'CONTRACT_EXPIRY',
  DOCUMENT: 'DOCUMENT_EXPIRY',
  FISCAL: 'FISCAL_OBLIGATION',
};

/** One dated obligation, whatever produced it. */
export interface ComplianceEvent {
  /** `SOURCE:id` — the React key, and unique across lanes because ids are not. */
  key: string;
  source: ComplianceSource;
  /** The deadline itself, as a DateOnly. Never derived: it is a column. */
  date: string;
  /** What is due, in the server's own words — a policy's insurer, an equipment's type, … */
  title: string;
  /** Where it sits, when the server said: the property's name, or null. */
  context: string | null;
  /** The row the lane's deep link opens. */
  sourceId: string;
  /** The property the row belongs to, for the calendar's property filter. */
  propertyId: string | null;
  /** Signed days to the deadline — the server's, except in the document lane (D-1142). */
  daysUntil: number | null;
}

/** A policy's cover running out. */
export function insuranceEvent(policy: InsuranceListItem): ComplianceEvent {
  return {
    key: `INSURANCE:${policy.id}`,
    source: 'INSURANCE',
    date: policy.endDate,
    title: policy.insurerName,
    context: policy.propertyName ?? null,
    sourceId: policy.id,
    propertyId: policy.propertyId,
    daysUntil: wireNumberOrNull(policy.daysUntilExpiry),
  };
}

/**
 * An installation's next revision.
 *
 * `title` is the equipment's type and `context` the property's name: „Centrală termică" beside
 * „Ap. 2 Aviației" is what a landlord recognises. The type's Romanian label is the caller's
 * `enums.EquipmentType.*`, so this function returns the raw member and never a sentence — except
 * for an „Alt echipament” the landlord named, whose title is that name, already in the landlord's
 * own words (D-2733).
 */
export function revisionEvent(equipment: EquipmentListItem): ComplianceEvent | null {
  if (equipment.nextRevisionDate == null) return null;

  return {
    key: `REVISION:${equipment.id}`,
    source: 'REVISION',
    date: equipment.nextRevisionDate,
    title: customTypeName(equipment) ?? equipment.equipmentType,
    context: equipment.propertyName ?? null,
    sourceId: equipment.id,
    propertyId: equipment.propertyId,
    daysUntil: wireNumberOrNull(equipment.daysUntilRevision),
  };
}

/**
 * A lease running out.
 *
 * An indeterminate contract has no end date and produces no event — 03 §8.2 makes `end_date`
 * null exactly then, and a calendar entry for a lease that does not end is a deadline nobody
 * owes.
 */
export function contractEvent(contract: ContractListItem): ComplianceEvent | null {
  if (contract.endDate == null || contract.isIndeterminate) return null;

  return {
    key: `CONTRACT:${contract.id}`,
    source: 'CONTRACT',
    date: contract.endDate,
    title: contract.contractNumber,
    context: contract.primaryTenantName ?? contract.propertyName,
    sourceId: contract.id,
    propertyId: contract.propertyId,
    daysUntil: wireNumberOrNull(contract.daysUntilEnd),
  };
}

/**
 * A document lapsing.
 *
 * `today` is a parameter rather than a call, so one screen renders one day for every row: the
 * subtraction is this client's only computed count and D-1142 records why the vault forces it.
 */
export function documentEvent(document: DocumentView, today: string): ComplianceEvent | null {
  if (document.expirationDate == null || document.expirationDate === '') return null;

  return {
    key: `DOCUMENT:${document.id}`,
    source: 'DOCUMENT',
    date: document.expirationDate,
    title: document.title,
    context: null,
    sourceId: document.id,
    propertyId: document.linkedEntityType === 'PROPERTY' ? document.linkedEntityId : null,
    daysUntil: daysUntilExpiration(document.expirationDate, today),
  };
}

/**
 * A fiscal term.
 *
 * NOT NULLABLE, unlike the other four mappers, and that is a fact about the endpoint rather
 * than an oversight: `FiscalCalendarEvent.Date` is a non-null `DateOnly` because
 * `FiscalCalendarComposer` never emits a row without one. An obligation whose `deadline` is
 * null is either a C168 with no event date or a chapter the engine can supply the shared D212
 * date for; everything else — a `NOT_REQUIRED` verdict, a CASS chapter of a year with no
 * declaration date — is `continue`d before it becomes an event. So the undated obligation the
 * other four lanes have to filter out never reaches this one.
 *
 * `daysUntil` IS THE SERVER'S `daysRemaining`, and it is dropped to null for the two kinds of
 * row the fiscal engine has itself decided are not alarms: one whose obligation is already
 * CLOSED (a C168 whose contract is registered) and one that is INFORMATIONAL (the PJ tenant's
 * own D100/D205). `FiscalCalendarComposer.Priority` downgrades exactly those two to INFO; the
 * unified calendar colours from the LADDER rather than from that field, and without this a
 * registered contract's lapsed 30-day term would be painted CRITICAL red on a screen whose
 * whole promise is that red means „do something today". A null countdown is `eventPriority`'s
 * own „nothing to say about this yet". See D-1074.
 */
export function fiscalEvent(event: FiscalCalendarEvent): ComplianceEvent {
  const settled = event.isClosed === true || event.informational === true;

  return {
    key: `FISCAL:${event.id}`,
    source: 'FISCAL',
    date: event.date,
    // The server's Romanian sentence, printed verbatim — `useEventLabel` gives the fiscal lane
    // no template for the reason 04 §8.11 gives every fiscal surface: the wording is composed
    // by `FiscalCalendarMessages` and a client that wrapped it would be writing fiscal copy.
    title: event.titleRo,
    context: event.propertyName ?? null,
    // The obligation, not the row: the calendar's own id is `{FORM}:{discriminator}:{date}` and
    // the surface a fiscal deadline opens is SCR-FISC-007's drawer, which is keyed on the
    // verdict. A reference-derived row — a building-tax instalment — has no verdict and no
    // link, exactly as a policy with no property has none (D-1137).
    sourceId: event.obligationId ?? '',
    propertyId: event.propertyId ?? null,
    daysUntil: settled ? null : wireNumber(event.daysRemaining),
  };
}

/**
 * The colour one event is worth, or null for a deadline the engine has not reached.
 *
 * @param event the row.
 * @param ladders one ladder per family, as `useComplianceLadders` read them.
 */
export function eventPriority(
  event: ComplianceEvent,
  ladders: Readonly<Record<ComplianceSource, RevisionLadder>>,
): NotificationPriority | null {
  return revisionPriority(event.daysUntil, ladders[event.source]);
}

/**
 * The events inside `[from, to]`, soonest first — one order for five lanes that each arrived in
 * their own.
 *
 * `from` is null for the agenda, whose first section is everything already lapsed: a window that
 * started today would hide exactly the rows a compliance calendar exists to shout about.
 * Sorting is by date and then by the key, so two deadlines on one day keep a stable order
 * across renders rather than swapping when a query refetches.
 */
export function inWindow(
  events: readonly ComplianceEvent[],
  from: string | null,
  to: string,
): ComplianceEvent[] {
  return [...events]
    .filter((event) => (from === null || event.date >= from) && event.date <= to)
    .sort((left, right) =>
      left.date === right.date
        ? left.key.localeCompare(right.key)
        : left.date.localeCompare(right.date),
    );
}

/** The events of one day, keyed by DateOnly — what a month grid's cells read. */
export function byDay(events: readonly ComplianceEvent[]): Map<string, ComplianceEvent[]> {
  const days = new Map<string, ComplianceEvent[]>();

  for (const event of events) {
    const existing = days.get(event.date);
    if (existing === undefined) days.set(event.date, [event]);
    else existing.push(event);
  }

  return days;
}

/** One agenda block: the month, and the events that fall in it. */
export interface MonthBlock {
  month: string;
  events: ComplianceEvent[];
}

/** The events gathered under the month they fall in, in the order they arrived. */
export function groupByMonth(events: readonly ComplianceEvent[]): MonthBlock[] {
  const blocks: MonthBlock[] = [];

  for (const event of events) {
    const month = event.date.slice(0, 7);
    const last = blocks.at(-1);
    if (last !== undefined && last.month === month) {
      last.events.push(event);
      continue;
    }
    blocks.push({ month, events: [event] });
  }

  return blocks;
}
